/**
 * LazyLoad — динамическая подгрузка script/style по требованию.
 *
 * Решает проблему cold-start: на 1-й экран (view-home) нужны
 * ~150 КБ JS + ~110 КБ CSS. Раньше в <head> загружалось ~673 KB JS +
 * 237 KB CSS (всё что когда-либо может понадобиться). При первом
 * заходе в view-profile/view-topup/etc. — соответствующий бандл
 * подгружается dynamic'ом, кэшируется по URL чтобы повторный
 * switchView не дёргал сеть.
 *
 * API:
 *   await LazyLoad.script('js/foo.js')                       → classic <script>
 *   await LazyLoad.script('js/tiptap.js', { type: 'module'}) → <script type=module>
 *   await LazyLoad.style('css/bar.css')                      → <link rel=stylesheet>
 *   await LazyLoad.bundle([
 *     'js/a.js',                              // classic
 *     'css/b.css',
 *     { url: 'js/tiptap.js', type: 'module' } // ES module
 *   ])
 *
 * Когда нужен `type: 'module'`:
 *   Если файл использует ТОП-УРОВНЕВЫЙ `import ...` (static ES imports).
 *   Без `type=module` браузер падает с SyntaxError на первом import,
 *   скрипт не выполняется, window.X-экспорт никогда не появляется.
 *   Грабли — см. инцидент 2026-05-17 (tiptap-init.js).
 *
 * Безопасность:
 *   - Каждый URL грузится РОВНО ОДИН РАЗ за сессию (cache в Map).
 *   - Параллельные вызовы возвращают тот же Promise.
 *   - Сохраняет порядок в DOM (appendChild в <head>).
 *   - При network-error rejected Promise НЕ кэшируется — next call retry.
 *   - Cache-bust: текущий window.APP_VERSION (фиксируется в index.html)
 *     добавляется как ?v= автоматически если src не содержит query.
 */
(function () {
    'use strict';

    const cache = new Map();  // url → Promise<void>

    /**
     * Cache-bust query. Приоритет:
     *   1. Per-file hash из window.APP_FILE_VERSIONS (инжектится бэком,
     *      содержит mtime-hash на каждый js/css). Только изменившиеся
     *      файлы получают новый ?v= → юзер ре-качает ТОЛЬКО их,
     *      остальное берётся из browser cache (immutable, 1y).
     *   2. window.APP_VERSION (git SHA) — fallback для файлов которых
     *      нет в map'е (например только что добавлен), и для legacy.
     *   3. Если ни того ни другого — URL без ?v= (dev режим без бэка).
     */
    function withVersion(url) {
        if (url.includes('?')) return url;
        // Нормализуем относительные пути — APP_FILE_VERSIONS хранит
        // ключи как 'js/foo.js' / 'css/bar.css'.
        const key = url.replace(/^\/+/, '');
        const fileHash = window.APP_FILE_VERSIONS?.[key];
        const version = fileHash || window.APP_VERSION;
        return version ? `${url}?v=${version}` : url;
    }

    function loadScript(src, opts = {}) {
        const url = withVersion(src);
        // Ключ кэша включает type: classic и module — это разные
        // <script>, не путаем (на практике ни один url не нужен в
        // обоих режимах, но защита от ошибки конфигурации).
        const cacheKey = opts.type === 'module' ? `module:${url}` : url;
        if (cache.has(cacheKey)) return cache.get(cacheKey);

        const p = new Promise((resolve, reject) => {
            const el = document.createElement('script');
            el.src = url;
            if (opts.type === 'module') {
                el.type = 'module';
                // ES module-скрипты defer-like по умолчанию (всегда async).
            } else {
                el.async = false;  // preserve eval order для classic
            }
            el.onload = () => resolve();
            el.onerror = () => {
                cache.delete(cacheKey);
                reject(new Error(`Failed to load script: ${url}`));
            };
            document.head.appendChild(el);
        });
        cache.set(cacheKey, p);
        return p;
    }

    function loadStyle(href) {
        const url = withVersion(href);
        if (cache.has(url)) return cache.get(url);

        const p = new Promise((resolve, reject) => {
            const el = document.createElement('link');
            el.rel = 'stylesheet';
            el.href = url;
            el.onload = () => resolve();
            el.onerror = () => {
                cache.delete(url);
                reject(new Error(`Failed to load style: ${url}`));
            };
            document.head.appendChild(el);
        });
        cache.set(url, p);
        return p;
    }

    function loadEntry(entry) {
        // Entry — либо строка (url), либо {url, type}.
        if (typeof entry === 'string') {
            if (entry.endsWith('.css')) return loadStyle(entry);
            if (entry.endsWith('.js')) return loadScript(entry);
            return Promise.reject(new Error(`Unknown asset type: ${entry}`));
        }
        if (entry && typeof entry === 'object' && entry.url) {
            if (entry.url.endsWith('.css')) return loadStyle(entry.url);
            if (entry.url.endsWith('.js')) return loadScript(entry.url, { type: entry.type });
            return Promise.reject(new Error(`Unknown asset type: ${entry.url}`));
        }
        return Promise.reject(new Error(`Invalid bundle entry: ${JSON.stringify(entry)}`));
    }

    async function loadBundle(entries) {
        // Загружаем параллельно, classic-скрипты держат порядок через async=false.
        await Promise.all(entries.map(loadEntry));
    }

    window.LazyLoad = {
        script: loadScript,
        style: loadStyle,
        bundle: loadBundle,
    };

    console.log('[LazyLoad] ✅ Initialized');
})();
